'use client';

import {usePathname} from 'next/navigation';
import {IQButton} from '@repo/ui/iq-button';
import {Dropdown} from '@repo/ui/dropdown';
import {FireIcon, MapIcon} from '@repo/ui/icons';
import {FRONTEND_URL} from 'config';
import {useAuth} from 'iq-auth';
import {useStreak} from 'iq-auth/hooks';
import {useIsMobile} from 'common/hooks/is_mobile';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ProfileImage({url, displayName = '?'}: {url?: string; displayName?: string}) {
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0]!.toUpperCase())
    .slice(0, 2)
    .join('');

  return (
    <span
      className='inline-flex items-center justify-center shrink-0 rounded-full bg-iq-grey-medium text-white overflow-hidden'
      style={{width: 20, height: 20, fontSize: 10, fontWeight: 600}}>
      {url ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={url} alt={displayName} loading='lazy' className='w-full h-full object-cover' />
      ) : (
        initials
      )}
    </span>
  );
}

const dashboardScreens = {
  feed: 'Feed',
  'my-interviews': 'My interviews',
  'my-comments': 'My comments',
  performance: 'Performance',
  'mock-interviews': 'Mock Interviews',
  profile: 'Profile',
} as const;

function DashboardNav() {
  return (
    <div className='py-1 min-w-[160px]'>
      {Object.entries(dashboardScreens).map(([slug, label]) => (
        <a
          key={slug}
          href={`${FRONTEND_URL}/dashboard/${slug}`}
          className='block px-4 py-2 text-sm text-iq-grey-dark hover:bg-iq-grey-lighter transition-colors'>
          {label}
        </a>
      ))}
    </div>
  );
}

function UpsellButton({size = 'sm', className}: {size?: 'sm' | 'md'; className?: string}) {
  const {user, isCoder} = useAuth();

  if (!user.loggedIn) return null;

  if (!user.hasSubscription || isCoder) {
    return (
      <IQButton
        variant='fill'
        size={size}
        color='orange'
        href={`${FRONTEND_URL}/pricing`}
        className={className}>
        Go Premium!
      </IQButton>
    );
  }

  return (
    <IQButton
      variant='fill'
      size={size}
      color='orange'
      href={`${FRONTEND_URL}/coaching`}
      className={className}>
      Get coaching
    </IQButton>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function HeaderUserNav() {
  const {isReady, user} = useAuth();
  const isMobile = useIsMobile();
  const pathname = usePathname();

  if (!isReady) return null;

  if (!user.loggedIn) {
    // Only login carries `next` (return the user to where they were). Signup
    // intentionally omits it so it falls back to its default destination (/pricing).
    const loginNext = `?next=${encodeURIComponent(pathname)}`;
    return (
      <Wrapper>
        <IQButton
          variant='outline'
          size='sm'
          color='orange'
          href={`${FRONTEND_URL}/login${loginNext}`}>
          Log in
        </IQButton>
        <IQButton variant='fill' size='sm' color='orange' href={`${FRONTEND_URL}/signup`}>
          Sign Up
        </IQButton>
      </Wrapper>
    );
  }

  const displayName = `${user.first_name} ${user.last_name}`.trim() || '?';

  if (isMobile) {
    return (
      <div className='flex flex-col w-full gap-2'>
        <IQButton
          variant='fill'
          size='sm'
          color='blue'
          href={`${FRONTEND_URL}/dashboard`}
          icon={<ProfileImage url={user.img_url} displayName={displayName} />}
          iconPlacement='right'
          className='w-full'>
          Dashboard
        </IQButton>
        <UpsellButton size='sm' className='w-full' />
      </div>
    );
  }

  return (
    <Wrapper>
      <div className='flex items-center gap-2 max-[1170]:hidden'>
        <UpsellButton />

        <IQButton
          variant='outline'
          size='sm'
          color='blue'
          href={`${FRONTEND_URL}/playlists/your-plan`}>
          <MapIcon color='#155e75' className='mr-1 shrink-0' />
          Your Plan
        </IQButton>
        <StreakButton />
      </div>
      <Dropdown
        label={<ProfileImage url={user.img_url} displayName={displayName} />}
        alignment='right'>
        <DashboardNav />
      </Dropdown>
    </Wrapper>
  );
}

const Wrapper = ({children}: {children: React.ReactNode}) => (
  <div className='flex items-center gap-2'>{children}</div>
);

function StreakButton() {
  const streak = useStreak();
  const color = streak > 0 ? 'orange' : 'gray';

  return (
    <span
      className={`inline-flex items-center gap-1 text-sm px-1 ${streak > 0 ? 'text-iq-orange' : 'text-gray-600'}`}>
      <FireIcon color={color} animate={streak > 0} className='w-4 h-4 -mt-1' />
      {streak}
    </span>
  );
}
